import { ExpandTaskBar } from "./expandedtaskbar";
import { skillGradeFor, skillIconNode } from "./itemicon";
import { itemArtNode } from "./itemart";
import {
  attachMouseModeWindows,
  type MouseButtonEvent,
  type MouseModeWindows,
} from "./mousebutton";
import { SLOT_ACTIVE_EFFECT_CSS, TEXTURE_ART, coolTimeBoxPolygon, elem, setSlotActive, styleInstaller } from "./chrome";


export const LEGACY_TASKBAR_HEIGHT = 37;

/** Pixel geometry from uiscript/uiscript/taskbar.py. */
export const LEGACY_TASKBAR_GEOMETRY = {
  width: "100vw",
  height: LEGACY_TASKBAR_HEIGHT,
  gauge: { left: 0, top: -10, width: 158, height: 47 },
  // Child coordinates are relative to Gauge_Board, matching HP/SP/STGauge_Board.
  meters: {
    hp: { left: 59, top: 14, width: 95, height: 11 },
    sp: { left: 59, top: 24, width: 95, height: 11 },
    stamina: { left: 59, top: 38, width: 95, height: 6 },
  },
  experience: { left: 158, top: 0, width: 105, height: 37 },
  leftMouse: { left: "50% - 128px", top: 3, width: 32, height: 32 },
  rightMouse: { left: "50% + 205px", top: 3, width: 32, height: 32 },
  quickBoard: { left: "50% - 86px", top: 0, width: 283, height: 37 },
  character: { right: 144, top: 3, width: 32, height: 32 },
  inventory: { right: 110, top: 3, width: 32, height: 32 },
  messenger: { right: 76, top: 3, width: 32, height: 32 },
  system: { right: 42, top: 3, width: 32, height: 32 },
} as const;

export interface TaskBarVitals {
  readonly hp: number;
  readonly maxHp: number;
  /** `player.HP_RECOVERY` -- health a drunk potion still owes
   *  (`uitaskbar.py:656`). It draws the translucent overlay behind the health
   *  gauge, not the gauge itself. */
  readonly hpRecovery: number;
  readonly sp: number;
  readonly maxSp: number;
  /** `player.SP_RECOVERY` (`uitaskbar.py:657`). */
  readonly spRecovery: number;
  readonly stamina: number;
  readonly maxStamina: number;
  readonly exp: number;
  readonly expToNext: number;
}

export interface TaskBarActions {
  onCharacter?: () => void;
  onInventory?: () => void;
  onMessenger?: () => void;
  onSystem?: () => void;
  /** The one button the expanded row reveals in this shipped pack -- toggle
   *  the dragon soul window (interfacemodule.py's ToggleDragonSoulWindow,
   *  bound to ExpandedTaskBar.BUTTON_DRAGON_SOUL). */
  onDragonSoul?: () => void;
  onLeftMouse?: () => void;
  onRightMouse?: () => void;
  onQuickSlot?: (index: number) => void;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
}

export type TaskBarQuickSlot =
  | {
      readonly kind: "item";
      readonly vnum: number;
      readonly name: string;
      readonly count: number;
      /** `ground.py:item_kind`'s byte, carried through from the bag slot this
       *  binding points at, so a vnum with no painting draws its class
       *  silhouette here as it does everywhere else. */
      readonly itemKind: number;
    }
  | { readonly kind: "skill"; readonly icon: string; readonly name: string; readonly level: number }
  /** `QUICKSLOT_TYPE_COMMAND` (`common/length.h:232`): a gesture, fired through
   *  `BINARY_ActEmotion` (`PythonPlayer.cpp:832-836`). The shipped pack draws
   *  these from `emotion.EMOTION_DICT`'s own icons, which this build has no
   *  copy of, so the cell carries the gesture's word. */
  | { readonly kind: "command"; readonly emotion: number; readonly name: string }
  | null;

/** What a drawn slot looks like, for the repaint check. One function so the
 *  before and after cannot disagree about what counts as a change. */
function quickSlotPrint(slot: TaskBarQuickSlot): string {
  if (slot === null) return "-";
  if (slot.kind === "item") return `i:${slot.vnum}:${slot.count}:${slot.name}`;
  if (slot.kind === "skill") return `s:${slot.icon}:${slot.level}:${slot.name}`;
  return `c:${slot.emotion}:${slot.name}`;
}

/** `Base_Board_01`'s tile (`locale/en/ui/taskbar.py:30`). */
const TASKBAR_BASE = `${TEXTURE_ART}/pattern/taskbar_base.png`;

/** One taskbar sprite, by the `.sub` its layout names. */
const bar = (name: string): string => `${TEXTURE_ART}/game/taskbar/${name}.png`;

/** `HPGauge`, `SPGauge` and `STGauge` are `ani_image`s over seven frames each,
 *  advanced every `delay` UI updates (`locale/en/ui/taskbar.py:135-149`,
 *  `:180-194`, `:215-229`; `CAniImageBox::OnUpdate`,
 *  `PythonWindow.cpp:1560-1575`). */
export const LEGACY_TASKBAR_GAUGE_FRAMES = 7;

/** `"delay" : 6` on each of the three (`locale/en/ui/taskbar.py:138`, `:183`,
 *  `:218`): six updates a frame, one update a rendered frame, so the whole
 *  loop is 42 frames -- 700 ms at the 60 Hz a browser paints at. */
export const LEGACY_TASKBAR_GAUGE_PERIOD_MS = 700;

/** `RampageGauge`'s sixteen frames, in the order the layout lists them --
 *  `10` is not one of them (`locale/en/ui/taskbar.py:58-73`). */
export const LEGACY_TASKBAR_RAMPAGE_FRAMES = [
  "00", "01", "02", "03", "04", "05", "06", "07",
  "08", "09", "11", "12", "13", "14", "15", "16",
] as const;

/** `"delay" : 6` again (`locale/en/ui/taskbar.py:52`), over sixteen frames:
 *  ninety-six rendered frames, 1600 ms at 60 Hz. */
export const LEGACY_TASKBAR_RAMPAGE_PERIOD_MS = 1600;

/** Where the sixteen live: `locale/en/ui/Mall/NN.sub`. */
const RAMPAGE_ART = `${TEXTURE_ART}/locale/mall`;

export const LEGACY_TASKBAR_GAUGE_ASSETS = {
  hp: `${TEXTURE_ART}/pattern/hpgauge`,
  sp: `${TEXTURE_ART}/pattern/spgauge`,
  stamina: `${TEXTURE_ART}/pattern/stgauge`,
} as const;

/** Each sprite by the name its layout gives it, so no rectangle is written
 *  here: the cut is `client/public/ui/ui_textures.json`'s. */
export const LEGACY_TASKBAR_SPRITES = {
  character: "character_button_01",
  inventory: "inventory_button_01",
  messenger: "community_button_01",
  system: "system_button_01",
  expand: "chat_button_01",
  mouse: "mouse_button_move_01",
  mouseHint: "mouse_button_camera_01",
  quickPageUp: "quickslot_upbutton_01",
  quickPageDown: "quickslot_downbutton_01",
  quickPageBoard: "quickslot_button_board",
  pageDigits: "1",
} as const;

/** The four page numerals `RefreshQuickSlot` swaps between
 *  (`root/uitaskbar.py:741-746`). */
export const LEGACY_TASKBAR_PAGE_DIGITS = ["1", "2", "3", "4"] as const;

/** The eight key labels the two quickslot rows draw at (3,3) of each cell
 *  (`locale/en/ui/taskbar.py:413-416`, `:441-444`). */
export const LEGACY_TASKBAR_SLOT_KEYS = [
  "1", "2", "3", "4", "f1", "f2", "f3", "f4",
] as const;

const STYLE_ID = "legacy-taskbar-style";

const CSS = `
.legacy-taskbar {
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  width: 100vw;
  height: 37px;
  z-index: 3;
  background: url("${TASKBAR_BASE}") repeat-x left top;
  pointer-events: none;
  font: 11px/1 system-ui, sans-serif;
  color: #f3e7c1;
}
.legacy-taskbar-vitals,
.legacy-taskbar-experience,
.legacy-taskbar-quickboard,
.legacy-taskbar-mouse,
.legacy-taskbar-right {
  position: absolute;
}
.legacy-taskbar-vitals {
  left: 0;
  top: -10px;
  width: 158px;
  height: 47px;
  background: url("${bar("gauge")}") left top no-repeat;
}
.legacy-taskbar-experience {
  left: 158px;
  top: 0;
  width: 105px;
  height: 37px;
  background: url("${bar("exp_gauge")}") left top no-repeat;
  pointer-events: auto;
}
/* RampageGauge at (8,4) of Gauge_Board, 40x40
   (locale/en/ui/taskbar.py:44-50). The layout declares it twice, one copy over
   the other, and the loader shows both (root/ui.py:3043); one element draws
   the same picture. */
.legacy-taskbar-rampage {
  position: absolute;
  left: 8px;
  top: 4px;
  width: 40px;
  height: 40px;
  background: left top / 40px 40px no-repeat;
  image-rendering: pixelated;
  pointer-events: none;
  animation: legacy-taskbar-rampage ${LEGACY_TASKBAR_RAMPAGE_PERIOD_MS}ms step-end infinite;
}
@keyframes legacy-taskbar-rampage {
${LEGACY_TASKBAR_RAMPAGE_FRAMES.map((frame, at) =>
  `  ${(at * 100 / LEGACY_TASKBAR_RAMPAGE_FRAMES.length).toFixed(3)}%`
  + ` { background-image: url("${RAMPAGE_ART}/${frame}.png"); }`).join("\n")}
}
.legacy-taskbar-meter {
  position: absolute;
  left: 59px;
  width: 95px;
  height: 11px;
  overflow: hidden;
  border: 0;
  padding: 0;
  background: rgba(0, 0, 0, .25);
  pointer-events: auto;
}
.legacy-taskbar-meter-fill {
  display: block;
  position: relative;
  z-index: 1;
  width: 0;
  height: 100%;
  transition: width 120ms linear;
}
/* HPRecoveryGaugeBar / SPRecoveryGaugeBar (uiscript/taskbar.py:87-95,
   :132-140): a plain 'bar' at 0,0 of the gauge's own 95px width, declared
   before the gauge image so the window loader draws it underneath -- only the
   stretch beyond the current fill is ever seen. Its 13px height overruns the
   11px gauge and is clipped by it, exactly as in the pack. The two colours are
   the pack's ARGB literals 0x55ff0000 and 0x550000ff, alpha 0x55/255. */
.legacy-taskbar-meter-recovery {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 0;
  width: 0;
  height: 13px;
}
.legacy-taskbar-meter-recovery[hidden] { display: none; }
.legacy-taskbar-meter-hp .legacy-taskbar-meter-recovery {
  background: rgba(255, 0, 0, .333);
}
.legacy-taskbar-meter-sp .legacy-taskbar-meter-recovery {
  background: rgba(0, 0, 255, .333);
}
.legacy-taskbar-meter-hp { top: 14px; }
.legacy-taskbar-meter-sp { top: 24px; }
.legacy-taskbar-meter-st { top: 38px; height: 6px; }
.legacy-taskbar-meter-hp .legacy-taskbar-meter-fill,
.legacy-taskbar-meter-sp .legacy-taskbar-meter-fill {
  background: left top / 95px 11px no-repeat;
}
.legacy-taskbar-meter-st .legacy-taskbar-meter-fill {
  background: left top / 95px 6px no-repeat;
}
.legacy-taskbar-meter-hp .legacy-taskbar-meter-fill {
  animation: legacy-taskbar-hpgauge ${LEGACY_TASKBAR_GAUGE_PERIOD_MS}ms step-end infinite;
}
@keyframes legacy-taskbar-hpgauge {
  0.0% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.hp}/01.png"); }
  14.286% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.hp}/02.png"); }
  28.571% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.hp}/03.png"); }
  42.857% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.hp}/04.png"); }
  57.143% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.hp}/05.png"); }
  71.429% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.hp}/06.png"); }
  85.714% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.hp}/07.png"); }
}
.legacy-taskbar-meter-sp .legacy-taskbar-meter-fill {
  animation: legacy-taskbar-spgauge ${LEGACY_TASKBAR_GAUGE_PERIOD_MS}ms step-end infinite;
}
@keyframes legacy-taskbar-spgauge {
  0.0% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.sp}/01.png"); }
  14.286% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.sp}/02.png"); }
  28.571% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.sp}/03.png"); }
  42.857% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.sp}/04.png"); }
  57.143% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.sp}/05.png"); }
  71.429% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.sp}/06.png"); }
  85.714% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.sp}/07.png"); }
}
.legacy-taskbar-meter-st .legacy-taskbar-meter-fill {
  animation: legacy-taskbar-stgauge ${LEGACY_TASKBAR_GAUGE_PERIOD_MS}ms step-end infinite;
}
@keyframes legacy-taskbar-stgauge {
  0.0% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.stamina}/01.png"); }
  14.286% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.stamina}/02.png"); }
  28.571% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.stamina}/03.png"); }
  42.857% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.stamina}/04.png"); }
  57.143% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.stamina}/05.png"); }
  71.429% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.stamina}/06.png"); }
  85.714% { background-image: url("${LEGACY_TASKBAR_GAUGE_ASSETS.stamina}/07.png"); }
}
.legacy-taskbar-meter-label,
.legacy-taskbar-exp-label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}
.legacy-taskbar-exp-fill {
  position: absolute;
  left: 5px;
  top: 9px;
  width: 95px;
  height: 19px;
  opacity: .95;
}
.legacy-taskbar-exp-point {
  position: absolute;
  top: 0;
  width: 19px;
  height: 19px;
  overflow: hidden;
  background: url("${bar("exp_gauge_point")}") left top no-repeat;
}
.legacy-taskbar-exp-point[hidden] { display: none; }
.legacy-taskbar-tooltip {
  position: fixed;
  z-index: 1;
  padding: 2px 4px;
  color: #f3e7c1;
  background: rgba(0, 0, 0, .72);
  border: 1px solid rgba(243, 231, 193, .24);
  border-radius: 3px;
  font: 11px/1 system-ui, sans-serif;
  pointer-events: none;
  transform: translate(-50%, -100%);
  white-space: nowrap;
}
.legacy-taskbar-exp-point:nth-child(1) { left: 0; }
.legacy-taskbar-exp-point:nth-child(2) { left: 25px; }
.legacy-taskbar-exp-point:nth-child(3) { left: 50px; }
.legacy-taskbar-exp-point:nth-child(4) { left: 75px; }
.legacy-taskbar-button,
.legacy-taskbar-quick {
  pointer-events: auto;
  appearance: none;
  border: 0;
  padding: 0;
  margin: 0;
  background-color: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
}
.legacy-taskbar-button:focus-visible,
.legacy-taskbar-quick:focus-visible {
  outline: 2px solid #ffe9a8;
  outline-offset: 1px;
}
.legacy-taskbar-quick:hover { filter: brightness(1.2); }
.legacy-taskbar-quick:active { filter: brightness(.82); }
/* Every button the layout gives an over and a down image gets both, by name:
   the four on the right (locale/en/ui/taskbar.py:319-321, :332-334, :345-347,
   :358-360), the chat expander (:387-389), the two quickslot page arrows
   (:475-477, :488-490) and the pair of mouse-mode buttons (:293-295, :304-306). */
${[
  ["character", LEGACY_TASKBAR_SPRITES.character],
  ["inventory", LEGACY_TASKBAR_SPRITES.inventory],
  ["messenger", LEGACY_TASKBAR_SPRITES.messenger],
  ["system", LEGACY_TASKBAR_SPRITES.system],
  ["expand", LEGACY_TASKBAR_SPRITES.expand],
  ["page-up", LEGACY_TASKBAR_SPRITES.quickPageUp],
  ["page-down", LEGACY_TASKBAR_SPRITES.quickPageDown],
  ["mouse", LEGACY_TASKBAR_SPRITES.mouse],
].map(([suffix, sprite]) => {
  const stem = sprite.replace(/_01$/, "");
  return `.legacy-taskbar-${suffix}:hover { background-image: url("${bar(`${stem}_02`)}"); }\n`
    + `.legacy-taskbar-${suffix}:active { background-image: url("${bar(`${stem}_03`)}"); }`;
}).join("\n")}
.legacy-mouse-mode-button:hover { background-image: var(--mouse-over); }
.legacy-mouse-mode-button:active { background-image: var(--mouse-down); }
.legacy-taskbar-mouse {
  top: 3px;
  width: 32px;
  height: 32px;
  background: url("${bar(LEGACY_TASKBAR_SPRITES.mouse)}") left top no-repeat;
}
.legacy-taskbar-left-mouse { left: calc(50% - 128px); }
.legacy-taskbar-right-mouse { left: calc(50% + 205px); }
/* The Ctrl hint image. uitaskbar.py:495-500 builds one floating TOP_MOST
   ImageBox from mouse_button_camera_01.sub at (xRight, screenHeight - 34) --
   xRight being the RightMouseButton's own global x, and the task bar sitting
   at screenHeight - 37, so -34 is 3 px down inside it: exactly over the right
   mouse button, which is at top 3px too. ShowMouseImage raises it (SetTop) and
   shows it; HideMouseImage hides it (uitaskbar.py:640-646). Pointer-events off
   because the original's is a float ImageBox and not a button: the button
   underneath keeps its click. */
.legacy-taskbar-mouse-hint {
  left: calc(50% + 205px);
  z-index: 4;
  pointer-events: none;
  background: url("${bar(LEGACY_TASKBAR_SPRITES.mouseHint)}") left top no-repeat;
}
.legacy-taskbar-mouse-hint[hidden] { display: none; }
.legacy-mouse-mode {
  position: absolute;
  z-index: 2;
  pointer-events: auto;
}
.legacy-mouse-mode[hidden] { display: none; }
.legacy-mouse-mode-button {
  position: absolute;
  width: 32px;
  height: 32px;
  appearance: none;
  border: 0;
  padding: 0;
  margin: 0;
  cursor: pointer;
  background-color: transparent;
}
.legacy-mouse-mode-button[hidden] { display: none; }
.legacy-mouse-mode-left .legacy-mouse-mode-button:nth-child(1) { top: 0; left: 0; }
.legacy-mouse-mode-left .legacy-mouse-mode-button:nth-child(2) { top: 32px; left: 0; }
.legacy-mouse-mode-left .legacy-mouse-mode-button:nth-child(3) { top: 64px; left: 0; }
.legacy-mouse-mode-right .legacy-mouse-mode-button:nth-child(1) { top: 0; left: 0; }
.legacy-mouse-mode-right .legacy-mouse-mode-button:nth-child(2) { top: 0; left: 32px; }
.legacy-mouse-mode-right .legacy-mouse-mode-button:nth-child(3) { top: 0; left: 64px; }
.legacy-taskbar-quickboard {
  left: calc(50% - 86px);
  top: 0;
  width: 283px;
  height: 37px;
}
.legacy-taskbar-quick {
  position: absolute;
  top: 3px;
  width: 32px;
  height: 32px;
  /* Public.dds:0,348,32x32, the same Slot_Base.sub used by the source
     grid_table. */
  background: url("${TEXTURE_ART}/public/slot_base.png") left top no-repeat;
}
.legacy-taskbar-quick:nth-child(1) { left: 0; }
.legacy-taskbar-quick:nth-child(2) { left: 32px; }
.legacy-taskbar-quick:nth-child(3) { left: 64px; }
.legacy-taskbar-quick:nth-child(4) { left: 96px; }
.legacy-taskbar-quick:nth-child(5) { left: 142px; }
.legacy-taskbar-quick:nth-child(6) { left: 174px; }
.legacy-taskbar-quick:nth-child(7) { left: 206px; }
.legacy-taskbar-quick:nth-child(8) { left: 238px; }
/* The key label is the sprite the layout puts at (3,3) of each cell, one per
   slot -- 1..4 on the first row and f1..f4 on the second
   (locale/en/ui/taskbar.py:413-416, :441-444). The text stays for a reader
   and is not painted. */
.legacy-taskbar-quick-key {
  position: absolute;
  left: 3px;
  top: 3px;
  height: 7px;
  font-size: 0;
  color: transparent;
  background: no-repeat left top;
  pointer-events: none;
}
${LEGACY_TASKBAR_SLOT_KEYS.map(
  (key) =>
    `.legacy-taskbar-quick-key[data-key="${key}"] {`
    + ` width: ${key.startsWith("f") ? "8" : "5"}px;`
    + ` background-image: url("${bar(key)}"); }`,
).join("\n")}
.legacy-taskbar-quick-count {
  position: absolute;
  left: 2px;
  bottom: 1px;
  color: #fff;
  font-size: 8px;
  font-weight: 700;
  text-shadow: 0 1px 2px #000;
  pointer-events: none;
}
.legacy-taskbar-quick-art {
  position: absolute;
  left: 3px;
  top: 3px;
  width: 26px;
  height: 26px;
  object-fit: contain;
  pointer-events: none;
}
.legacy-taskbar-quick-art.skill-icon {
  width: 26px;
  height: 26px;
}
/* RenderCoolTimeBox's colour, D3DXCOLOR(0, 0, 0, 0.5) (PythonGraphic.cpp:498),
   over the whole 32x32 slot (PythonSlotWindow.cpp:1055). */
.legacy-taskbar-quick-cool {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .5);
  pointer-events: none;
}
/* Keep the source Slot_Base.dds frame visible behind any item/skill art. */
.legacy-taskbar-quick::after {
  content: "";
  position: absolute;
  inset: 0;
  border: 1px solid rgba(255, 255, 255, .08);
  pointer-events: none;
}
.legacy-taskbar-expand {
  position: absolute;
  left: 128px;
  top: 1px;
  width: 14px;
  height: 35px;
  background: url("${bar(LEGACY_TASKBAR_SPRITES.expand)}") left top no-repeat;
}

.legacy-taskbar-page {
  position: absolute;
  left: 272px;
  top: 0;
  width: 11px;
  height: 37px;
}
.legacy-taskbar-page-board {
  position: absolute;
  left: 1px;
  top: 15px;
  width: 9px;
  height: 8px;
  background: url("${bar(LEGACY_TASKBAR_SPRITES.quickPageBoard)}") left top no-repeat;
}
.legacy-taskbar-page-button {
  position: absolute;
  left: 1px;
  width: 9px;
  height: 5px;
  background: url("${bar(LEGACY_TASKBAR_SPRITES.quickPageUp)}") left top no-repeat;
}
.legacy-taskbar-page-up { top: 9px; }
.legacy-taskbar-page-down {
  top: 24px;
  background-image: url("${bar(LEGACY_TASKBAR_SPRITES.quickPageDown)}");
}
.legacy-taskbar-page-number {
  position: absolute;
  left: 3px;
  top: 15px;
  width: 5px;
  height: 7px;
  background: url("${bar("1")}") left top no-repeat;
}
/* One rule per page numeral, from LEGACY_TASKBAR_PAGE_DIGITS -- the same four
   .sub rectangles RefreshQuickSlot swaps between (root/uitaskbar.py:741-746). */
${LEGACY_TASKBAR_PAGE_DIGITS.map(
  (digit, page) =>
    `.legacy-taskbar-page-number[data-page="${String(page)}"] { background-image: url("${bar(digit)}"); }`,
).join("\n")}
.legacy-taskbar-right {
  top: 3px;
  width: 32px;
  height: 32px;
}
.legacy-taskbar-character { right: 144px; background: url("${bar(LEGACY_TASKBAR_SPRITES.character)}") left top no-repeat; }
.legacy-taskbar-inventory { right: 110px; background: url("${bar(LEGACY_TASKBAR_SPRITES.inventory)}") left top no-repeat; }
.legacy-taskbar-messenger { right: 76px; background: url("${bar(LEGACY_TASKBAR_SPRITES.messenger)}") left top no-repeat; }
.legacy-taskbar-system { right: 42px; background: url("${bar(LEGACY_TASKBAR_SPRITES.system)}") left top no-repeat; }
@media (max-width: 620px) {
  /* Preserve the desktop source geometry, but give a phone a safe 48px hit
     strip and leave the attack button's bottom-right reach unobstructed. */
  .legacy-taskbar { height: 48px; }
  .legacy-taskbar-right { display: none; }
  .legacy-taskbar-vitals { top: 1px; transform: scale(.82); transform-origin: left top; }
  .legacy-taskbar-experience { display: none; }
  .legacy-taskbar-mouse { display: none; }
  .legacy-taskbar-quickboard { left: 50%; transform: translateX(-50%); top: 5px; }
  .legacy-taskbar-expand, .legacy-taskbar-page { display: none; }
  .legacy-taskbar-expanded { display: none; }
  .legacy-taskbar-quick { top: 3px; }
}
@media (prefers-reduced-motion: reduce) {
  .legacy-taskbar-meter-fill { transition: none; animation: none; }
}
`;

const installStyle = styleInstaller(STYLE_ID, CSS + SLOT_ACTIVE_EFFECT_CSS);

function fraction(value: number, total: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, Math.min(1, value / total));
}

function button(doc: Document, className: string, label: string, action?: () => void): HTMLButtonElement {
  const result = doc.createElement("button");
  result.type = "button";
  result.className = className;
  result.setAttribute("aria-label", label);
  result.title = label;
  if (action !== undefined) result.addEventListener("click", action);
  return result;
}

export class TaskBar {
  private readonly root: HTMLElement;
  private readonly hp: HTMLElement;
  private readonly sp: HTMLElement;
  private readonly stamina: HTMLElement;
  private readonly experience: HTMLElement;
  private readonly exp: HTMLElement;
  private readonly expPoints: HTMLElement[] = [];
  private readonly quickCells: HTMLButtonElement[] = [];
  private readonly pageNumber: HTMLElement;
  private readonly tooltip: HTMLElement;
  /** The Ctrl hint image (`uitaskbar.py:495-500`). */
  private readonly mouseHint: HTMLElement;
  private readonly expandTaskBar: ExpandTaskBar;
  private hovered: HTMLElement | null = null;
  private quickSlots: readonly TaskBarQuickSlot[] | null = null;
  private readonly mouseMode: MouseModeWindows;
  private leftMouseEvent: MouseButtonEvent = "move_and_attack";
  private rightMouseEvent: MouseButtonEvent = "camera";

  constructor(container: HTMLElement, actions: TaskBarActions = {}) {
    const doc = container.ownerDocument ?? document;
    installStyle(doc);

    this.root = elem(doc, "div", "legacy-taskbar");
    this.root.setAttribute("role", "toolbar");
    this.root.setAttribute("aria-label", "Game controls");

    const vitals = elem(doc, "div", "legacy-taskbar-vitals");
    const rampage = elem(doc, "div", "legacy-taskbar-rampage");
    rampage.setAttribute("aria-hidden", "true");
    vitals.append(rampage);
    this.hp = this.meter(doc, vitals, "hp", "HP");
    this.sp = this.meter(doc, vitals, "sp", "SP");
    // The legacy taskbar labels this meter with TASKBAR_ST (“ST”), which is
    // also what its hover tooltip displays (`uitaskbar.py:707`;
    // `locale_game.txt:562`). Keep the visible/accessible label source-exact.
    this.stamina = this.meter(doc, vitals, "st", "ST");
    this.root.append(vitals);

    const experience = elem(doc, "div", "legacy-taskbar-experience");
    // Same accessible shape as the HP/SP/ST `meter()` helper: role and label
    // on the outer element a reader (or a check) finds, not on the inner fill
    // span it happens to be built from.
    experience.setAttribute("role", "meter");
    experience.setAttribute("aria-label", "Experience");
    this.experience = experience;
    this.exp = elem(doc, "span", "legacy-taskbar-exp-fill");
    for (let index = 0; index < 4; index++) {
      const point = elem(doc, "span", "legacy-taskbar-exp-point");
      point.hidden = true;
      this.expPoints.push(point);
      this.exp.append(point);
    }
    const expLabel = elem(doc, "span", "legacy-taskbar-exp-label", "Experience");
    experience.append(this.exp, expLabel);
    this.root.append(experience);

    this.tooltip = elem(doc, "div", "legacy-taskbar-tooltip");
    this.tooltip.hidden = true;

    // `HELP_MOUSE_LEFT`/`HELP_MOUSE_RIGHT` (`locale_interface.txt:156-157`) --
    // the shipped Help window's own captions for these two buttons, not a
    // house name for what they do.
    const leftMouse = button(
      doc,
      "legacy-taskbar-button legacy-taskbar-mouse legacy-taskbar-left-mouse",
      "Function of the Left Mouse button",
      () => {
        this.mouseMode.toggleLeft();
        actions.onLeftMouse?.();
      },
    );
    const rightMouse = button(
      doc,
      "legacy-taskbar-button legacy-taskbar-mouse legacy-taskbar-right-mouse",
      "Function of the Right Mouse button",
      () => {
        this.mouseMode.toggleRight();
        actions.onRightMouse?.();
      },
    );
    // Built here rather than lazily so `SetTop`/`Show` is a `hidden` flip and
    // nothing has to be constructed on a key press.
    this.mouseHint = elem(doc, "div", "legacy-taskbar-mouse legacy-taskbar-mouse-hint");
    this.mouseHint.hidden = true;
    this.root.append(leftMouse, rightMouse, this.mouseHint);
    this.mouseMode = attachMouseModeWindows(this.root, (hand, event) => {
      if (hand === "left") this.leftMouseEvent = event;
      else this.rightMouseEvent = event;
    });

    const quickboard = elem(doc, "div", "legacy-taskbar-quickboard");
    // `HELP_QUICKSLOT` (`locale_interface.txt:169`): the shipped name for this
    // row is "Fast Access Fields", never "Quick slots".
    for (let index = 0; index < 8; index++) {
      const quick = button(doc, "legacy-taskbar-quick", `Fast Access Field ${index + 1}`, () => actions.onQuickSlot?.(index));
      // Original labels 1-4 / F1-F4 (`locale/en/ui/taskbar.py`). F1-F4 are
      // local slots 4-7 (`game.py:330-333`); `data-quick-index` is what a
      // bag or skill drag reads so a drop binds instead of hitting the ground.
      quick.dataset.key = index < 4 ? String(index + 1) : `F${index - 3}`;
      quick.dataset.quickIndex = String(index);
      this.quickCells.push(quick);
      quickboard.append(quick);
    }
    // `TASKBAR_EXPAND` (`locale_interface.txt`) -- this pack's `taskbar.py`
    // names this position "ExpandButton", not "ChatButton"; it reveals the
    // `ExpandedTaskBar` row below rather than opening chat (Enter already
    // does that, unconditionally, elsewhere in this build).
    const expand = button(doc, "legacy-taskbar-button legacy-taskbar-expand", "Expand window", () => {
      this.expandTaskBar.toggle();
    });
    quickboard.append(expand);
    const page = elem(doc, "div", "legacy-taskbar-page");
    const pageBoard = elem(doc, "span", "legacy-taskbar-page-board");
    const pageNumber = elem(doc, "span", "legacy-taskbar-page-number");
    this.pageNumber = pageNumber;
    // `TASKBAR_PREV_QUICKSLOT`/`TASKBAR_NEXT_QUICKSLOT` (`locale_interface.txt
    // :318-319`) name the shipped concept "Fast Access Field"; their bracketed
    // keybindings are the original's own keyboard shortcuts, not this build's,
    // so only the noun phrase is carried over.
    const pageUp = button(doc, "legacy-taskbar-button legacy-taskbar-page-button legacy-taskbar-page-up", "Previous Fast Access Field", actions.onPreviousPage);
    const pageDown = button(doc, "legacy-taskbar-button legacy-taskbar-page-button legacy-taskbar-page-down", "Next Fast Access Field", actions.onNextPage);
    page.append(pageBoard, pageNumber, pageUp, pageDown);
    quickboard.append(page);
    this.root.append(quickboard);

    this.root.append(
      button(doc, "legacy-taskbar-button legacy-taskbar-right legacy-taskbar-character", "Character", actions.onCharacter),
      button(doc, "legacy-taskbar-button legacy-taskbar-right legacy-taskbar-inventory", "Inventory", actions.onInventory),
      button(doc, "legacy-taskbar-button legacy-taskbar-right legacy-taskbar-messenger", "Friends", actions.onMessenger),
      button(doc, "legacy-taskbar-button legacy-taskbar-right legacy-taskbar-system", "Options", actions.onSystem),
    );

    // `ExpandTaskBar` is its own 37×37 window (`expandedtaskbar.py`), a
    // sibling of this bar, not a child. Hidden until Expand is clicked.
    this.expandTaskBar = new ExpandTaskBar(container, {
      onDragonSoul: actions.onDragonSoul,
    });

    this.root.append(this.tooltip);
    container.append(this.root);
    this.bindTooltip(this.hp);
    this.bindTooltip(this.sp);
    this.bindTooltip(this.stamina);
    this.bindTooltip(this.experience);
    // The source's empty slots still show their number key. Paint that shell
    // before the first wire snapshot so a cold HUD is recognizable and usable.
    this.setQuickSlots([]);
    // `game.py:84` starts `quickSlotPageIndex` at 0, and the numeral box shows
    // the page one-based (`taskbar/1.sub` for page 0).
    this.setQuickPage(0);
  }

  get element(): HTMLElement {
    return this.root;
  }

  setMouseHintShown(shown: boolean): void {
    this.mouseHint.hidden = !shown;
  }

  /** Whether the Ctrl hint image is up. */
  get mouseHintShown(): boolean {
    return !this.mouseHint.hidden;
  }

  /** `player.SetMouseFunc` for MBT_LEFT (`uitaskbar.py:1084-1133`). */
  get leftMouseFunc(): MouseButtonEvent {
    return this.leftMouseEvent;
  }

  /** `player.SetMouseFunc` for MBT_RIGHT. */
  get rightMouseFunc(): MouseButtonEvent {
    return this.rightMouseEvent;
  }

  /** `DragonSoulGiveQuilification` (`interfacemodule.py`) swaps the button's
   *  tooltip from `TASKBAR_DISABLE` to `TASKBAR_DRAGON_SOUL` once the server
   *  grants the one-way qualification (`char_dragonsoul.cpp:50-60`). Never
   *  reversed, matching the server flag it mirrors. */
  setDragonSoulQualified(qualified: boolean): void {
    this.expandTaskBar.setDragonSoulQualified(qualified);
  }

  setQuickPage(page: number): void {
    const shown = Math.min(Math.max(page, 0), LEGACY_TASKBAR_PAGE_DIGITS.length - 1);
    this.pageNumber.dataset.page = String(shown);
    this.pageNumber.title = `Fast Access Field page ${String(shown + 1)}`;
  }

  /** Paint authoritative item/skill art into the legacy slots. This is
   * intentionally separate from update(vitals): inventory and skill frames
   * have their own clocks, and an unchanged slot should not churn its DOM. */
  setQuickSlots(slots: readonly TaskBarQuickSlot[]): void {
    const next = slots.slice(0, this.quickCells.length);
    const fingerprint = next.map(quickSlotPrint).join("|");
    const previous =
      this.quickSlots === null ? null : this.quickSlots.map(quickSlotPrint).join("|");
    this.quickSlots = next;
    if (fingerprint === previous) return;
    for (let index = 0; index < this.quickCells.length; index++) {
      const cell = this.quickCells[index];
      const slot = next[index] ?? null;
      const key = elem(
        this.root.ownerDocument,
        "span",
        "legacy-taskbar-quick-key",
        cell.dataset.key ?? String(index + 1),
      );
      key.dataset.key = (cell.dataset.key ?? String(index + 1)).toLowerCase();
      if (slot === null) {
        cell.replaceChildren(key);
        cell.title = `Fast Access Field ${key.textContent} — empty`;
        continue;
      }
      const art =
        slot.kind === "item"
          ? itemArtNode(slot.vnum, slot.itemKind, this.root.ownerDocument)
          : slot.kind === "skill"
            ? skillIconNode(slot.icon, skillGradeFor(slot.level), this.root.ownerDocument)
            : null;
      const children: Node[] = [key];
      if (art !== null) {
        art.classList.add("legacy-taskbar-quick-art");
        children.push(art);
      }
      if (slot.kind === "command") {
        children.push(
          elem(this.root.ownerDocument, "span", "legacy-taskbar-quick-art", slot.name),
        );
        cell.title = `${key.textContent} — ${slot.name}`;
      } else if (slot.kind === "item") {
        const count = elem(
          this.root.ownerDocument,
          "span",
          "legacy-taskbar-quick-count",
          String(Math.max(0, slot.count)),
        );
        children.push(count);
        cell.title = `${key.textContent} — ${slot.name} (${String(Math.max(0, slot.count))})`;
      } else {
        cell.title = `${key.textContent} — ${slot.name} (level ${String(Math.max(0, slot.level))})`;
      }
      cell.replaceChildren(...children);
    }
  }

  /** Cover each cell by the fraction of its skill's cool time already gone,
   *  null for a cell that is not cooling (`uitaskbar.py:801-804`). */
  setCoolTimes(elapsed: readonly (number | null)[]): void {
    for (let index = 0; index < this.quickCells.length; index++) {
      const cell = this.quickCells[index];
      const polygon = coolTimeBoxPolygon(elapsed[index] ?? 1);
      let cover = cell.querySelector<HTMLElement>(".legacy-taskbar-quick-cool");
      if (polygon === null) {
        cover?.remove();
        continue;
      }
      if (cover === null) {
        cover = elem(this.root.ownerDocument, "span", "legacy-taskbar-quick-cool");
        cell.append(cover);
      }
      cover.style.setProperty("clip-path", polygon);
    }
  }

  /** Light each cell whose skill is an active toggle: `OnActivateSkill` and
   *  `OnDeactivateSkill` (`uitaskbar.py:897-935`). */
  setActiveSlots(active: readonly boolean[]): void {
    this.quickCells.forEach((cell, index) => setSlotActive(cell, active[index] === true));
  }

  update(model: TaskBarVitals): void {
    this.setMeter(this.hp, model.hp, model.maxHp);
    this.setRecovery(this.hp, model.hp, model.hpRecovery, model.maxHp);
    this.setMeter(this.sp, model.sp, model.maxSp);
    this.setRecovery(this.sp, model.sp, model.spRecovery, model.maxSp);
    this.setMeter(this.stamina, model.stamina, model.maxStamina);
    this.setExperience(model.exp, model.expToNext);
  }

  private meter(doc: Document, parent: HTMLElement, kind: string, label: string): HTMLElement {
    const meter = elem(doc, "span", `legacy-taskbar-meter legacy-taskbar-meter-${kind}`);
    meter.setAttribute("role", "meter");
    meter.setAttribute("aria-label", label);
    meter.dataset.tooltipLabel = label;
    if (kind !== "st") {
      // Stamina has no recovery pool: the original's taskbar declares the two
      // overlay bars under the health and spirit gauges only
      // (`uiscript/taskbar.py:87`, `:132`), and `TaskBar.SetST` takes no
      // recovery argument at all (`uitaskbar.py:703`).
      const recovery = elem(doc, "span", "legacy-taskbar-meter-recovery");
      recovery.hidden = true;
      meter.append(recovery);
    }
    const fill = elem(doc, "span", "legacy-taskbar-meter-fill");
    meter.append(fill);
    const hidden = elem(doc, "span", "legacy-taskbar-meter-label", label);
    meter.append(hidden);
    parent.append(meter);
    return meter;
  }

  private setMeter(meter: HTMLElement, value: number, total: number): void {
    const safeValue = Number.isFinite(value) ? Math.max(0, value) : 0;
    const safeTotal = Number.isFinite(total) ? Math.max(0, total) : 0;
    const progress = fraction(safeValue, safeTotal);
    const fill = meter.querySelector<HTMLElement>(".legacy-taskbar-meter-fill");
    if (fill !== null) fill.style.width = `${(progress * 95).toFixed(1)}px`;
    meter.setAttribute("aria-valuenow", String(safeValue));
    meter.setAttribute("aria-valuemax", String(safeTotal));
    const label = meter.dataset.tooltipLabel ?? "";
    const shown = Math.min(safeValue, safeTotal);
    this.setTooltip(meter, safeTotal > 0 ? `${label} : ${shown} / ${safeTotal}` : "");
  }

  private setRecovery(
    meter: HTMLElement,
    value: number,
    recovery: number,
    total: number,
  ): void {
    const bar = meter.querySelector<HTMLElement>(".legacy-taskbar-meter-recovery");
    if (bar === null) return;
    const safeTotal = Number.isFinite(total) ? Math.max(0, total) : 0;
    if (safeTotal <= 0) return;
    const safeRecovery = Number.isFinite(recovery) ? Math.max(0, recovery) : 0;
    if (safeRecovery === 0) {
      bar.hidden = true;
      return;
    }
    const safeValue = Number.isFinite(value) ? Math.max(0, value) : 0;
    const current = Math.min(safeValue, safeTotal);
    const destination = Math.min(safeTotal, current + safeRecovery);
    bar.style.width = `${Math.floor(95 * (destination / safeTotal))}px`;
    bar.hidden = false;
  }

  private setExperience(value: number, total: number): void {
    const safeTotal = Number.isFinite(total) ? Math.max(0, Math.floor(total)) : 0;
    const supplied = Number.isFinite(value) ? Math.floor(value) : 0;
    const safeValue = Math.max(0, Math.min(supplied, safeTotal));
    // Python 2's `/` in the legacy code is integer division for these integer
    // status values. With a next-level total below four it shows no lamp.
    const quarter = Math.floor(safeTotal / this.expPoints.length);
    const full = quarter === 0 ? 0 : Math.min(this.expPoints.length, Math.floor(safeValue / quarter));

    for (let index = 0; index < this.expPoints.length; index++) {
      const point = this.expPoints[index];
      const amount = index < full
        ? 1
        : index === full && quarter !== 0
          ? (safeValue % quarter) / quarter
          : 0;
      point.hidden = amount <= 0;
      // `SetRenderingRect(0, amount - 1, 0, 0)` crops the top of an
      // expanded image while leaving its bottom at the source position. Keep
      // that bottom-up fill in CSS instead of shrinking the lamp sideways.
      point.style.top = `${((1 - amount) * 19).toFixed(1)}px`;
      point.style.height = `${(amount * 19).toFixed(1)}px`;
      point.style.backgroundPosition = `-487px -${((1 - amount) * 19).toFixed(1)}px`;
    }
    this.exp.setAttribute("aria-valuenow", String(safeValue));
    this.exp.setAttribute("aria-valuemax", String(safeTotal));
    // The outer gauge (`role="meter"`, set at construction) carries the same
    // pair, so a reader of the meter itself -- not only its inner fill span --
    // finds a maximum.
    this.experience.setAttribute("aria-valuenow", String(safeValue));
    this.experience.setAttribute("aria-valuemax", String(safeTotal));
    // `tooltipEXP.SetText("%s : %.2f%%" % (localeInfo.TASKBAR_EXP, ...))`
    // (`uitaskbar.py:735`); `TASKBAR_EXP` is "Experience" (`locale_game.txt
    // :558`), never the abbreviation.
    this.setTooltip(
      this.experience,
      safeTotal > 0 ? `Experience : ${((safeValue / safeTotal) * 100).toFixed(2)}%` : "",
    );
  }

  private bindTooltip(target: HTMLElement): void {
    target.addEventListener("mouseenter", (event) => this.showTooltip(target, event));
    target.addEventListener("mousemove", (event) => this.showTooltip(target, event));
    target.addEventListener("mouseleave", () => {
      if (this.hovered !== target) return;
      this.hovered = null;
      this.tooltip.hidden = true;
    });
  }

  private showTooltip(target: HTMLElement, event: MouseEvent): void {
    const text = target.dataset.tooltip ?? "";
    if (text === "") return;
    this.hovered = target;
    this.tooltip.textContent = text;
    this.tooltip.style.left = `${event.clientX}px`;
    this.tooltip.style.top = `${event.clientY - 15}px`;
    this.tooltip.hidden = false;
  }

  private setTooltip(target: HTMLElement, text: string): void {
    target.dataset.tooltip = text;
    if (this.hovered !== target) return;
    this.tooltip.textContent = text;
    this.tooltip.hidden = text === "";
  }
}
